import { z } from 'zod';
import { SettingsSchema } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';

export const ContentRequestBodySchema = z.object({
  retailerSku: z.string(),
  profileId: z.string(),
  profileVersion: z.number()
});

export const ContentGenerationStatusSchema = z.object({
  skuContentGenerationId: z.string(),
  status: z.string() // Could be enum if we know all possible statuses
});

export const ContentGenerationsSchema = z.record(z.string(), ContentGenerationStatusSchema);

export const GenerateContentForSkuRequestSchema = z.object({
  retailerId: z.number(),
  retailerSku: z.string(),
  stacklineSku: z.string(),
  useDynamicProfile: z.boolean(),
  settings: SettingsSchema
});

export const GenerateContentForSkusRequestSchema = z.object({
  beaconClientId: z.number(),
  retailerId: z.number(),
  contentRequests: z.array(
    z.object({
      retailerSku: z.string(),
      profileId: z.string(),
      profileVersion: z.number()
    })
  ),
  projectId: z.string().optional()
});

export const GenerateContentForSkuResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: z.object({
    skuContentGenerationId: z.string(),
    status: z.string() // Could be defined as an enum if we have all possible statuses
  })
});

export const GenerateContentForSkusResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: z.object({
    contentGenerations: z.record(
      z.string(),
      z.object({
        skuContentGenerationId: z.string(),
        status: z.string()
      })
    )
  })
});

const GeneratedContentSchema = z.object({
  title: z.string(),
  featureBullets: z.array(z.string()),
  description: z.string()
});

export const ContentGenStatus = [
  'NO_GENERATION',
  'GENERATING',
  'READY_TO_REVIEW',
  'REVIEWED',
  'GENERATION_ERROR'
] as const;

const ParseContentGenerationItemSchema = z.object({
  status: z.enum(ContentGenStatus),
  contentUrl: z.string(),
  content: GeneratedContentSchema,
  libraryStatus: z.string()
});

const ContentGenerationItemSchema = z.object({
  status: z.enum(ContentGenStatus),
  contentUrl: z.string(),
  content: z.string(),
  profileId: z.string(),
  profileVersion: z.number().optional(),
  libraryStatus: z.string()
});

// Updated schema in apiTypes.ts
export const GetContentGenerationStatusResponseSchema = z.object({
  data: z.object({
    success: z.boolean(),
    data: z.record(z.string(), ContentGenerationItemSchema)
  })
});

export const GetContentGenerationStatusRequestSchema = z.object({
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  beaconClientId: z.number(),
  skuContentGenerationIds: z.array(z.string())
});

export const GetCurrentContentRequestSchema = z.object({
  retailerSku: z.string(),
  retailerId: z.number(),
  weekId: z.number(),
  skuContentGenerationId: z.string()
});

/**
 * Notes: debug property is omitted, as it is not needed for the frontend
 */
export const GetCurrentContentResponseSchema = z.object({
  bullets: z.array(z.string()),
  title: z.string(),
  description: z.string(),
  imageUrls: z.array(z.string().url()),
  videoUrls: z.array(z.string().url()),
  retailerId: z.number(),
  retailerSku: z.string(),
  timestamp: z.number(),
  weekId: z.number(),
  aplusScore: z.number(),
  stacklineSku: z.string()
});

export interface Keyword {
  keyword: string;
  is_sl_keyword: boolean;
  is_client_keyword: boolean;
}
export interface Phrase {
  phrase: string;
  groupId?: string;
}
interface WeightedKeywordPhrases {
  sections: KeywordSection[];
}

interface ProductKeywordData {
  retailerId: number;
  retailerSku: string;
  isSuccess: boolean;
  message: string | null;
  weightedKeywordPhrases: WeightedKeywordPhrases;
  contentType: string | null;
}

export interface GetProductKeywordResponse {
  isSuccess: boolean;
  data: ProductKeywordData;
}

export const GetProductKeywordRequestSchema = z.object({
  retailerSku: z.string().min(1, 'Retailer SKU is required'),
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  weekId: z.number().int().positive('Week ID must be a positive integer'),
  contentMetricType: z.string(),
  bulletPoints: z.array(z.string()),
  skuContentGenerationId: z.string(),
  title: z.string(),
  description: z.string()
});

export const ContentLibraryManagementRequestSchema = z.object({
  skuContentGenerationId: z.string(),
  retailerId: z.number(),
  retailerSku: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  bullets: z.array(z.string()).optional()
});

type ProfileKeywords = {
  inclusion: string[];
  exclusion: string[];
  shouldIncludeStacklineRecommendedKeywords: boolean;
};

type ProfileCharacterLimits = {
  bullets: number;
  descriptions: number;
  titles: number;
};

type ProfileToneVoice = {
  type: string;
  intensity: string;
};

type ProfileSettings = {
  bulletPointsCount: number;
  characterLimits: ProfileCharacterLimits;
  keywordDensity: number;
  guidelines: string[];
  aiInstructions?: string;
  keywords: ProfileKeywords;
  tone: ProfileToneVoice;
  voice: ProfileToneVoice;
};

type ProfileConfig = {
  beaconClientId: number;
  profileId: string;
  profileName: string;
  profileDescription: string;
  isCustomProfile: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  settings: ProfileSettings;
  profileType: string;
  status: string;
  version: number;
};

type ContentLibraryManagementResponseData = {
  beaconClientId: number;
  retailerId: number;
  retailerSku: string;
  contentUrl: string;
  profileId: string;
  profileVersion: number;
  profileName: string;
  profileConfig: ProfileConfig;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
};

export type ContentLibraryManagementResponse = {
  data: ContentLibraryManagementResponseData;
  status: number;
};

export type ContentRequestBody = z.infer<typeof ContentRequestBodySchema>;
export type ContentGenerationStatus = z.infer<typeof ContentGenerationStatusSchema>;
export type ContentGenerations = z.infer<typeof ContentGenerationsSchema>;
export type GenerateContentForSkuRequest = z.infer<typeof GenerateContentForSkuRequestSchema>;
export type GenerateContentForSkuResponse = z.infer<typeof GenerateContentForSkuResponseSchema>;
export type GenerateContentForSkusRequest = z.infer<typeof GenerateContentForSkusRequestSchema>;
export type GetContentGenerationStatusResponse = z.infer<typeof GetContentGenerationStatusResponseSchema>;
export type GetContentGenerationStatusRequest = z.infer<typeof GetContentGenerationStatusRequestSchema>;
export type GetCurrentContentRequest = z.infer<typeof GetCurrentContentRequestSchema>;
export type GetCurrentContentResponse = z.infer<typeof GetCurrentContentResponseSchema>;
export type ContentGenerationItem = z.infer<typeof ContentGenerationItemSchema>;
export type ParseContentGenerationItem = z.infer<typeof ParseContentGenerationItemSchema>;
export type GeneratedContent = z.infer<typeof GeneratedContentSchema>;
export type GetProductKeywordRequest = z.infer<typeof GetProductKeywordRequestSchema>;
export type ContentLibraryManagementRequest = z.infer<typeof ContentLibraryManagementRequestSchema>;
export type GenerateContentForSkusResponse = z.infer<typeof GenerateContentForSkusResponseSchema>;

export const ContentLibraryManagementResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    beaconClientId: z.number(),
    retailerId: z.number(),
    retailerSku: z.string(),
    contentUrl: z.string(),
    profileId: z.string(),
    profileVersion: z.number(),
    profileName: z.string(),
    profileConfig: z.object({
      beaconClientId: z.number(),
      profileId: z.string(),
      profileName: z.string(),
      profileDescription: z.string(),
      isCustomProfile: z.boolean(),
      createdBy: z.string(),
      createdAt: z.string(),
      updatedBy: z.string(),
      updatedAt: z.string(),
      settings: SettingsSchema,
      profileType: z.string(),
      status: z.string(),
      version: z.number()
    }),
    status: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.string(),
    updatedBy: z.string()
  })
});

export const GetProfileRequestSchema = z.object({
  profileId: z.string(),
  version: z.number().optional()
});

export const GetProfileResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: z.object({
    beaconClientId: z.number(),
    profileId: z.string(),
    profileName: z.string(),
    profileDescription: z.string(),
    isCustomProfile: z.boolean(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedBy: z.string(),
    updatedAt: z.string(),
    settings: SettingsSchema,
    profileType: z.string(),
    status: z.string(),
    version: z.number()
  })
});

export type GetProfileRequest = z.infer<typeof GetProfileRequestSchema>;
export type GetProfileResponse = z.infer<typeof GetProfileResponseSchema>;

// Export Content API Types
export const ExportContentRequestSchema = z.object({
  retailerId: z.number(),
  skuContentGenerationIds: z.array(z.string())
});

export const ContentBreakdownSchema = z.object({
  title: z.string(),
  description: z.string(),
  bullets: z.array(z.string())
});

export const ContentExportItemSchema = z.object({
  skuContentGenerationId: z.string(),
  retailerId: z.number(),
  retailerSku: z.string().nullable(),
  current: ContentBreakdownSchema.nullable(),
  new: ContentBreakdownSchema.nullable()
});

export const ExportContentResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: z.array(ContentExportItemSchema)
});

export type ExportContentRequest = z.infer<typeof ExportContentRequestSchema>;
export type ContentBreakdown = z.infer<typeof ContentBreakdownSchema>;
export type ContentExportItem = z.infer<typeof ContentExportItemSchema>;
export type ExportContentResponse = z.infer<typeof ExportContentResponseSchema>;

// New comparison types for ProductKeywords comparison
export const ContentMetricType = {
  KeywordOrganicTraffic: 0,
  KeywordSearchVolume: 1
} as const;

export type ContentMetricType = (typeof ContentMetricType)[keyof typeof ContentMetricType];

export const ContentMetricTypeSchema = z.nativeEnum(ContentMetricType);

// Content Generation Metrics API Types
export const ContentGenerationMetricType = {
  ContentUtilizationScore: 'ContentUtilizationScore',
  KeywordUsageScore: 'KeywordUsageScore',
  KeywordPopularityScore: 'KeywordPopularityScore',
  KeywordProductivityScore: 'KeywordProductivityScore'
} as const;

export type ContentGenerationMetricType = (typeof ContentGenerationMetricType)[keyof typeof ContentGenerationMetricType];

export const ContentGenerationMetricTypeSchema = z.nativeEnum(ContentGenerationMetricType);

export const GetContentGenerationMetricsRequestSchema = z.object({
  retailerSku: z.string().min(1, 'Retailer SKU is required'),
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  weekId: z.number().int().positive('Week ID must be a positive integer'),
  stacklineSku: z.string().optional(),
  profileId: z.string().optional(),
  profileVersion: z.number().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  bullets: z.array(z.string()).optional(),
  metricType: ContentGenerationMetricTypeSchema
});

export const GetContentGenerationMetricsResponseSchema = z.object({
  data: z.object({
    score: z.number().optional(),
    matchedKeywords: z.array(z.string()).optional(),
    expectedKeywordsCount: z.number().optional(),
    keywordCount: z.number().optional(),
    averageCountOfKeywords: z.number().optional(),
    averageCountOfKeywordsGeneratingTraffic: z.number().optional()
  })
});

export const ProductKeywordsPayloadSchema = z.object({
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  retailerSku: z.string().min(1, 'Retailer SKU is required'),
  weekId: z.number().int().positive('Week ID must be a positive integer'),
  subcategoryId: z.number().int().positive().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  bulletPoints: z.array(z.string()).optional(),
  contentMetricType: ContentMetricTypeSchema.optional(),
  skuContentGenerationId: z.string().optional()
});

export const ProductKeywordsComparisonRequestSchema = z.object({
  current: ProductKeywordsPayloadSchema,
  new: ProductKeywordsPayloadSchema
});

export const KeywordContentSchema = z.object({
  phrase: z.string(),
  weight: z.number().optional(),
  frequency: z.number().optional(),
  groupId: z.string().optional()
});

const KeywordWeightSchema = z.object({
  keyword: z.string(),
  is_sl_keyword: z.boolean(),
  is_client_keyword: z.boolean()
});

export const KeywordGroupSchema = z.object({
  groupId: z.string(),
  weights: z.array(KeywordWeightSchema)
});

export const KeywordSectionSchema = z.object({
  name: z.string(),
  content: z.array(KeywordContentSchema),
  groups: z.array(KeywordGroupSchema)
});

export const ProductAnalysisSchema = z.object({
  sections: z.array(KeywordSectionSchema)
});

export const KeywordSummarySchema = z.object({
  total_keywords_current: z.number(),
  total_keywords_new: z.number(),
  keywords_added: z.number(),
  keywords_removed: z.number(),
  keywords_retained: z.number(),
  net_keyword_change: z.number()
});

export const KeywordChangesSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
  retained: z.array(z.string())
});

export const ContentLengthChangesSchema = z.object({
  current: z.record(z.string(), z.number()),
  new: z.record(z.string(), z.number()),
  changes: z.record(
    z.string(),
    z.object({
      character_change: z.number(),
      word_change: z.number()
    })
  )
});

export const SectionImprovementsSchema = z.record(
  z.string(),
  z.object({
    phrase_change: z.number(),
    group_change: z.number(),
    keyword_change: z.number(),
    keywords_added: z.array(z.string()),
    keywords_removed: z.array(z.string())
  })
);

export const OriginalContentSchema = z.object({
  current: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    bullet_points: z.array(z.string()).optional()
  }),
  new: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    bullet_points: z.array(z.string()).optional()
  })
});

export const ComparisonInsightsSchema = z.object({
  summary: KeywordSummarySchema,
  keyword_changes: KeywordChangesSchema,
  content_length_changes: ContentLengthChangesSchema,
  section_improvements: SectionImprovementsSchema,
  original_content: OriginalContentSchema,
  summary_text: z.string()
});

export const ComparisonAnalysisSchema = z.object({
  current_analysis: ProductAnalysisSchema,
  new_analysis: ProductAnalysisSchema,
  comparison: ComparisonInsightsSchema
});

export const ComparisonResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: ComparisonAnalysisSchema,
  message: z.string().optional()
});

// Inferred types from Zod schemas
export type ProductKeywordsPayload = z.infer<typeof ProductKeywordsPayloadSchema>;
export type ProductKeywordsComparisonRequest = z.infer<typeof ProductKeywordsComparisonRequestSchema>;
export type KeywordContent = z.infer<typeof KeywordContentSchema>;
export type KeywordWeight = z.infer<typeof KeywordWeightSchema>;
export type KeywordGroup = z.infer<typeof KeywordGroupSchema>;
export type KeywordSection = z.infer<typeof KeywordSectionSchema>;
export type ProductAnalysis = z.infer<typeof ProductAnalysisSchema>;
export type KeywordSummary = z.infer<typeof KeywordSummarySchema>;
export type KeywordChanges = z.infer<typeof KeywordChangesSchema>;
export type ContentLengthChanges = z.infer<typeof ContentLengthChangesSchema>;
export type SectionImprovements = z.infer<typeof SectionImprovementsSchema>;
export type OriginalContent = z.infer<typeof OriginalContentSchema>;
export type ComparisonInsights = z.infer<typeof ComparisonInsightsSchema>;
export type ComparisonAnalysis = z.infer<typeof ComparisonAnalysisSchema>;
export type ComparisonResponse = z.infer<typeof ComparisonResponseSchema>;

// GetContentGenerationRequestsByStatus API Types
export const GetContentGenerationRequestsByStatusRequestSchema = z.object({
  retailerId: z.number(),
  status: z.array(z.string()).optional(),
  retailerSkus: z.array(z.string()).optional()
});

export type GetContentGenerationRequestsByStatusRequest = z.infer<
  typeof GetContentGenerationRequestsByStatusRequestSchema
>;

// GetContentGenerationRequestsByProject API Types
export const GetContentGenerationRequestsByProjectRequestSchema = z.object({
  retailerId: z.number(),
  projectId: z.string().optional(),
  status: z.array(z.string()).optional(),
  retailerSkus: z.array(z.string()).optional(),
  excludeRetailerSkus: z.array(z.string()).optional(),
  createdBy: z.string().optional(),
  pageNumber: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(10000).optional(),
  searchTerm: z.string().max(100).optional()
});

export type GetContentGenerationRequestsByProjectRequest = z.infer<
  typeof GetContentGenerationRequestsByProjectRequestSchema
>;

// GetContentLibrary API Types
export const GetContentLibraryRequestSchema = z.object({
  retailerId: z.number().positive(),
  searchTerm: z.string().max(100).optional(),
  retailerSkus: z.array(z.string()).optional(),
  brandIds: z.array(z.number()).optional(),
  pageNumber: z.number().min(1).optional(),
  pageSize: z.number().min(1).max(1000).optional(),
  filterNullLibraryStatus: z.boolean().optional(),
  projectId: z.string().nullable().optional()
});

export const MasterContentRecordSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  bullets: z.array(z.string()).optional(),
  imageUrls: z.array(z.string()).optional(),
  videoUrls: z.array(z.string()).optional(),
  retailerId: z.number().optional(),
  retailerSku: z.string().optional(),
  stacklineSku: z.string().optional(),
  weekId: z.number().optional(),
  timestamp: z.number().optional(),
  aplusScore: z.number().optional(),
  updatedAt: z.string().optional(),
  debug: z.any().optional()
});

export const ContentVersionRecordSchema = z.object({
  version: z.string(),
  type: z.enum(['generated', 'master']),
  updatedAt: z.string(),
  skuContentGenerationId: z.string(),
  updatedBy: z.string().optional(),
  createdBy: z.string(),
  profileId: z.string().optional(),
  profileName: z.string().optional(),
  projectId: z.string().optional(),
  projectName: z.string().optional(),
  libraryStatus: z.string(),
  status: z.string(),
  content: z
    .object({
      title: z.string().optional(),
      description: z.string().optional(),
      bullets: z.array(z.string()).optional()
    })
    .optional()
});

export const ContentLibraryRecordSchema = z.object({
  retailerSku: z.string(),
  stacklineSku: z.string(),
  productName: z.string().optional(),
  createdBy: z.string(),
  lastUpdatedAt: z.string(),
  lastUpdatedBy: z.string().optional(),
  totalVersions: z.number(),
  versions: z.array(ContentVersionRecordSchema),
  masterContent: MasterContentRecordSchema.optional(),
  currentContent: MasterContentRecordSchema.optional()
});

export const GetContentLibraryResponseSchema = z.object({
  records: z.array(ContentLibraryRecordSchema),
  totalCount: z.number(),
  pageNumber: z.number(),
  pageSize: z.number()
});

export type GetContentLibraryRequest = z.infer<typeof GetContentLibraryRequestSchema>;
export type MasterContentRecord = z.infer<typeof MasterContentRecordSchema>;
export type ContentVersionRecord = z.infer<typeof ContentVersionRecordSchema>;
export type ContentLibraryRecord = z.infer<typeof ContentLibraryRecordSchema>;
export type GetContentLibraryResponse = z.infer<typeof GetContentLibraryResponseSchema>;

// Streaming Summary API Types - Updated to match new specification
export const StreamContentSchema = z.object({
  title: z.string(),
  description: z.string(),
  bullet_points: z.array(z.string())
});

export const StreamCompareProductKeywordsSummaryRequestSchema = z.object({
  current_analysis: z.object({
    sections: z.array(z.any()) // Accept any section structure
  }),
  new_analysis: z.object({
    sections: z.array(z.any()) // Accept any section structure
  }),
  current: StreamContentSchema.optional(), // Optional content structure
  new: StreamContentSchema.optional() // Optional content structure
});

export type StreamContent = z.infer<typeof StreamContentSchema>;
export type StreamCompareProductKeywordsSummaryRequest = z.infer<
  typeof StreamCompareProductKeywordsSummaryRequestSchema
>;

// Credit Status Types
export const CreditStatusSchema = z.object({
  creditsAvailable: z.number(),
  creditsInUse: z.number(),
  creditsUsed: z.number()
});

export const CreditStatusResponseSchema = z.object({
  success: z.boolean(),
  data: CreditStatusSchema
});

export const CreateProjectRequestSchema = z.object({
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  name: z.string(),
  profileId: z.string(),
  profileVersion: z.number(),
  profileName: z.string()
});

export const UpdateProjectRequestSchema = z.object({
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  projectId: z.string(),
  beaconClientId: z.number(),
  name: z.string().optional(),
  profileId: z.string().optional(),
  profileVersion: z.number().optional(),
  profileName: z.string().optional()
});

export const ProjectDataSchema = z.object({
  projectId: z.string(),
  name: z.string(),
  beaconClientId: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string()
});

export const UpdateProjectResponseSchema = z.object({
  success: z.boolean(),
  data: ProjectDataSchema.optional(),
  error: z.string().optional(),
  missingFields: z.array(z.string()).optional()
});

export const GetProjectsRequestSchema = z.object({
  retailerId: z.number().int().positive('Retailer ID must be a positive integer'),
  createdBy: z.string().optional(),
  pageNumber: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(1000).optional().default(50)
});

export const ProjectRecordSchema = z.object({
  projectId: z.string(),
  timestamp: z.string().optional(),
  beaconClientId: z.number().optional(),
  createdAt: z.string().optional(),
  createdBy: z.string(),
  description: z.string().optional(),
  name: z.string(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
  profileId: z.string().optional(),
  profileName: z.string().optional(),
  profileVersion: z.number().optional()
});

export const GetProjectsResponseSchema = z.object({
  records: z.array(ProjectRecordSchema),
  totalCount: z.number().int(),
  debug: z.object({}).optional()
});

export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;
export type ProjectData = z.infer<typeof ProjectDataSchema>;
export type UpdateProjectResponse = z.infer<typeof UpdateProjectResponseSchema>;
export type GetProjectsRequest = z.infer<typeof GetProjectsRequestSchema>;
export type ProjectRecord = z.infer<typeof ProjectRecordSchema>;
export type GetProjectsResponse = z.infer<typeof GetProjectsResponseSchema>;
export type CreditStatus = z.infer<typeof CreditStatusSchema>;
export type CreditStatusResponse = z.infer<typeof CreditStatusResponseSchema>;
export type GetContentGenerationMetricsRequest = z.infer<typeof GetContentGenerationMetricsRequestSchema>;
export type GetContentGenerationMetricsResponse = z.infer<typeof GetContentGenerationMetricsResponseSchema>;

// Edit Generated Content API Types
export const EditGeneratedContentRequestSchema = z.object({
  beaconClientId: z.number(),
  skuContentGenerationId: z.string(),
  content: z.object({
    title: z.string(),
    featureBullets: z.array(z.string()),
    description: z.string()
  })
});

export const EditGeneratedContentResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: ContentGenerationItemSchema.optional(),
  message: z.string().optional()
});

export type EditGeneratedContentRequest = z.infer<typeof EditGeneratedContentRequestSchema>;
export type EditGeneratedContentResponse = z.infer<typeof EditGeneratedContentResponseSchema>;
